import { useIntl } from "react-intl";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useEffect, useMemo, useState } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import getColumns from "./columns";
import { useNavigate } from "react-router-dom";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { Manager, ManagerGroups, ManagerGroupsEnum } from "@lib/types/Manager";
import { ManagerModal } from "@lib/components/PageComponents/Manager";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { Radio, TableProps } from "antd";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { TableHeaderFilter } from "@lib/types/Api";
import { useTypedSelector } from "@lib/redux";
import { AppContext, AuthRoles, SharedContextProps } from "@lib/types";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  apiAddManager,
  apiDeleteManager,
  apiGetManagers,
  apiUpdateManager,
} from "@lib/api/manager";
import { toast } from "react-toastify";

const SharedManagers: React.FC<SharedContextProps> = ({
  context,
  detailContext,
  customTitle,
  customerId,
  providerId,
  pageIsActive = true,
  withoutContainer = false,
  minusHeight,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { user } = useTypedSelector((state) => state.auth);
  const [group, setGroup] = useState<ManagerGroupsEnum | null>(null);
  const isAdmin = user?.role === AuthRoles.Admin;

  useEffect(() => {
    if (!context) {
      setGroup(
        ManagerGroups.find((g) => g.group.includes(user.role))?.key || null,
      );
    } else {
      setGroup(null);
    }
  }, [context]);

  const initialFilters = [
    ...(context === AppContext.CUSTOMER && !detailContext
      ? [`role||$eq||${AuthRoles.CustomerAgent}`]
      : []),
    ...(context === AppContext.PAYMENT_PROVIDER && !detailContext
      ? [`role||$eq||${AuthRoles.ProviderAgent}`]
      : []),
    ...(customerId ? [`customerId||$eq||${customerId}`] : []),
    ...(providerId ? [`providerId||$eq||${providerId}`] : []),
  ];

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
    filter: initialFilters,
    join: ["customer", "provider"],
  });

  useEffect(() => {
    if (group) {
      setParams({
        ...params,
        filter: [
          `role||$in||${ManagerGroups.find((g) => g.key === group)?.group.join(",")}`,
        ],
      });
    }
  }, [group]);

  const managersQuery = useQuery({
    queryFn: () => apiGetManagers(params),
    queryKey: ["manager", JSON.stringify(params)],
    enabled: !!params && pageIsActive,
  });
  const { data, isLoading, isFetching, refetch } = managersQuery;
  const apiData = data?.data?.data;

  const addManagerMutation = useMutation({
    mutationFn: apiAddManager,
    onSuccess() {
      toast.success(
        (messages["common.createdMessage"] as string).replace(
          "{name}",
          messages["common.manager"] as string,
        ),
      );
      refetch();
    },
  });

  const updateManagerMutation = useMutation({
    mutationFn: apiUpdateManager,
    onSuccess() {
      toast.success(
        (messages["common.updatedMessage"] as string).replace(
          "{name}",
          messages["common.manager"] as string,
        ),
      );
      refetch();
    },
  });

  const deleteManagerMutation = useMutation({
    mutationFn: apiDeleteManager,
    onSuccess() {
      toast.success(
        (messages["common.deletedMessage"] as string).replace(
          "{name}",
          messages["common.manager"] as string,
        ),
      );
      refetch();
    },
  });

  const [openModal, setModalOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState<Manager>();

  useEffect(() => {
    if (!openModal) setSelectedManager(null);
  }, [openModal]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleSelectManager = (manager: Manager) => {
    setSelectedManager(manager);
    toggle(true);
  };

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ ...params, page, limit });
    }
  };

  const onChange: TableProps<Manager>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        group,
        context,
        detailContext,
        handleSelectManager,
        navigate,
        deleteQuery: deleteManagerMutation,
        openModal,
        apiData,
        params,
        messages,
        userRole: user?.role,
      }),
    [
      apiData,
      deleteManagerMutation,
      openModal,
      handleSelectManager,
      params,
      user?.role,
    ],
  );
  // console.log("🚀 ~ ", { context, group });

  const hiddenFilters = useMemo(() => {
    const filterNames = [];
    if (!!detailContext) {
      filterNames.push("customerId", "providerId");
    }
    if (group === ManagerGroupsEnum.ADMIN) {
      filterNames.push("role");
    }
    if (group === ManagerGroupsEnum.OTHERS) {
      filterNames.push("role");
    }
    if (context) {
      filterNames.push("role");
    }
    return filterNames;
  }, [detailContext, group, context]);

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !hiddenFilters.includes(f.key)) as TableHeaderFilter[];
  }, [params]);

  const title = customTitle || (messages["common.systemUsers"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      noContainer={!!withoutContainer}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      {!context && !detailContext && isAdmin && (
        <AppsHeader>
          <StyledFlex>
            <Radio.Group
              value={group}
              onChange={(e) => setGroup(e.target.value)}
            >
              <Radio value={ManagerGroupsEnum.ADMIN}>
                {messages["common.admin"] as string}
              </Radio>
              <Radio value={ManagerGroupsEnum.OTHERS}>
                {messages["common.others"] as string}
              </Radio>
            </Radio.Group>
          </StyledFlex>
        </AppsHeader>
      )}

      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={managersQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          plusProps={{
            onClick: () => toggle(true),
            disabled: isLoading,
            loading: isLoading,
          }}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          onChange={onChange}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
          size="small"
          minusHeight={minusHeight}
        />
      </AppsContent>

      <ManagerModal
        mutationQuery={
          selectedManager ? updateManagerMutation : addManagerMutation
        }
        openModal={openModal}
        toggleModal={toggle}
        manager={selectedManager}
        preSelectedCustomer={customerId}
        preSelectedProvider={providerId}
        context={context}
      />
    </AppsContainer>
  );
};

export default SharedManagers;
